import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader, useToast } from 'components/admin';
import RichTextEditor from 'components/admin/RichTextEditor';
import { Button, Checkbox, Input, Select, Textarea } from 'components/common';
import { createIntroduceCapacity, getAdminIntroduceCapacityById, getAdminIntroduceMenus, updateIntroduceCapacity } from 'apis/adminIntroduce';
import {
    useActiveLanguagesQuery,
    useAdminCreateMutation,
    useAdminDetailQuery,
    useAdminListQuery,
    useAdminUpdateMutation,
} from 'hooks';
import '../../../styles/admin-shared.scss';

const IntroduceCapacitiesForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const [activeTab, setActiveTab] = useState(0);
    const [formData, setFormData] = useState({ introduceMenuId: '', iconImage: '', sortOrder: 0, isActive: true, translation: [] });

    const { data: languages = [] } = useActiveLanguagesQuery();
    const { data: menuData = { items: [] } } = useAdminListQuery('introduce-menus-options', { pageSize: 1000, languageCode: 'vi' }, getAdminIntroduceMenus);
    const menus = menuData.items;
    const { data: detail, error: detailError } = useAdminDetailQuery('introduce-capacities', id, getAdminIntroduceCapacityById);
    const createMutation = useAdminCreateMutation('introduce-capacities', createIntroduceCapacity);
    const updateMutation = useAdminUpdateMutation('introduce-capacities', updateIntroduceCapacity);
    const loading = createMutation.isPending || updateMutation.isPending;

    useEffect(() => {
        if (!id && languages.length) setFormData(prev => ({ ...prev, translation: languages.map(l => ({ languageId: l.id, title: '', subtitle: '', description: '', extraDescription: '' })) }));
    }, [id, languages]);

    useEffect(() => {
        if (detail) setFormData({ ...detail, introduceMenuId: detail.introduceMenuId || '' });
    }, [detail]);

    useEffect(() => {
        if (detailError) toast.error(detailError.message || 'Tải dữ liệu thất bại');
    }, [detailError, toast]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };
    const handleTranslationChange = (langId, field, value) => {
        setFormData(prev => ({ ...prev, translation: prev.translation.map(t => t.languageId === langId ? { ...t, [field]: value } : t) }));
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...formData, introduceMenuId: Number(formData.introduceMenuId), sortOrder: Number(formData.sortOrder) };
            const res = id
                ? await updateMutation.mutateAsync({ id, data: payload })
                : await createMutation.mutateAsync(payload);
            if (res.success) { toast.success(id ? 'Cập nhật thành công' : 'Thêm mới thành công'); navigate('/admin/introduce-capacities'); }
            else toast.error(res.message || 'Lưu thất bại');
        } catch { toast.error('Lưu thất bại'); }
    };

    return (
        <div className="admin-page">
            <PageHeader title={id ? 'Cập nhật Năng lực' : 'Thêm Năng lực'} />
            <form className="form-card" onSubmit={handleSubmit}>
                <p className="form-section-title">Cài đặt</p>
                <div className="form-row">
                    <Select
                        label="Menu Giới thiệu *"
                        name="introduceMenuId"
                        value={formData.introduceMenuId}
                        onChange={handleChange}
                        options={[{ value: '', label: 'Chọn menu...' }, ...menus.map(m => {
                            const activeLangId = languages[activeTab]?.id;
                            const t = m.translations?.find(tr => tr.languageId === activeLangId) || m.translations?.[0];
                            return { value: m.id, label: t?.name || t?.title || m.name || `Menu ${m.id}` };
                        })]}
                        required
                    />
                    <Input label="Thứ tự sắp xếp" name="sortOrder" type="number" value={formData.sortOrder} onChange={handleChange} />
                </div>
                {/* <Input label="URL Hình ảnh Icon" name="iconImage" value={formData.iconImage} onChange={handleChange} placeholder="https://..." /> */}
                <Checkbox label="Hoạt động" name="isActive" checked={formData.isActive} onChange={handleChange} />
                {languages.length > 0 && (
                    <>
                        <p className="form-section-title">Bản dịch</p>
                        <div className="translation-tabs">
                            <div className="tab-header">{languages.map((lang, i) => <button key={lang.id} type="button" className={`tab-btn ${activeTab === i ? 'active' : ''}`} onClick={() => setActiveTab(i)}>{lang.name}</button>)}</div>
                            {languages.map((lang, i) => {
                                const t = formData.translation.find(t => t.languageId === lang.id) || {};
                                return activeTab === i ? (
                                    <div key={lang.id} className="tab-content">
                                        <Input label="Tiêu đề *" value={t.title || ''} onChange={e => handleTranslationChange(lang.id, 'title', e.target.value)} required />
                                        <Input label="Tiêu đề phụ" value={t.subtitle || ''} onChange={e => handleTranslationChange(lang.id, 'subtitle', e.target.value)} />
                                        <div className="admin-form-group">
                                            <label>Mô tả</label>
                                            <RichTextEditor
                                                value={t.description || ''}
                                                onChange={(content) => handleTranslationChange(lang.id, 'description', content)}
                                                placeholder={`Nhập mô tả (${lang.name})...`}
                                            />
                                        </div>
                                        <div className="admin-form-group">
                                            <label>Mô tả thêm</label>
                                            <RichTextEditor
                                                value={t.extraDescription || ''}
                                                onChange={(content) => handleTranslationChange(lang.id, 'extraDescription', content)}
                                                placeholder={`Nhập mô tả thêm (${lang.name})...`}
                                            />
                                        </div>
                                    </div>
                                ) : null;
                            })}
                        </div>
                    </>
                )}
                <div className="form-actions">
                    <Button type="submit" disabled={loading}>{loading ? 'Đang lưu...' : id ? 'Cập nhật' : 'Thêm mới'}</Button>
                    <Button type="button" variant="secondary" onClick={() => navigate('/admin/introduce-capacities')}>Hủy</Button>
                </div>
            </form>
        </div>
    );
};

export default IntroduceCapacitiesForm;
